import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.scss';
import ChatBot from './components/Chatbot/Chatbot';
import { GoogleOAuthProvider } from '@react-oauth/google';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import { lazy, Suspense, useContext, useEffect } from 'react';
import importRetry from './importRetry';
import lazyWithRetry from './lazyWithRetry';
import 'react-toastify/dist/ReactToastify.css';
import { QueryClientProvider } from '@tanstack/react-query';
import { QueryCache, QueryClient } from '@tanstack/react-query';
import { userContext } from './UserContext';
import ProtectedRoute from './privateRoutes/ProtectedRoute';
import { ROUTE_PERMISSIONS } from './privateRoutes/routePermissions';
import Employees from './pages/Employees/Employees';

import EmployeeTaskDetails from './pages/Task/EmployeeTaskDetails';
export const QUERY_CLIENT = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error: any, query) => {
      if (query.state.data !== undefined) {
        console.error(`Something went wrong`);
      }
      if (error?.status === 403) {
        window.location.href = '/';
      }
    },
  }),
});

/* ---------------- LAZY COMPONENTS ---------------- */

const ToastContainer = lazy(() =>
  import('react-toastify').then(({ ToastContainer }) => ({
    default: ToastContainer,
  }))
);

const Onboard = lazyWithRetry(() =>
  importRetry(() => import('./pages/Onboard/Onboard'))
);

const Inprocess = lazyWithRetry(() =>
  importRetry(() => import('./pages/inprocess/Inprocess'))
);

const LeaveManagement = lazyWithRetry(() =>
  importRetry(() => import('./components/LeaveManagement/LeaveManagement'))
);

const Layout = lazyWithRetry(() => importRetry(() => import('./layouts')));

const PageNotFound = lazyWithRetry(() =>
  importRetry(() => import('./pages/PageNotFound/PageNotFound'))
);

const Home = lazyWithRetry(() =>
  importRetry(() => import('./pages/Home/Home'))
);

const SalarySlip = lazyWithRetry(() =>
  importRetry(() => import('./pages/SalarySlip/SalarySlip'))
);

const Profile = lazyWithRetry(() =>
  importRetry(() => import('./pages/Profile/Profile'))
);

const PolicyManager = lazyWithRetry(() =>
  importRetry(() => import('./pages/PolicyManager/PolicyManager'))
);

const Agent = lazyWithRetry(() =>
  importRetry(() => import('./pages/Agent/UpdateAgent'))
);

const AttendanceList = lazyWithRetry(() =>
  importRetry(() => import('./pages/Attendance/AttendanceList'))
);

const AttendenceDetail = lazyWithRetry(() =>
  importRetry(() => import('./pages/Attendance/AttendenceDetails'))
);

const UsersOnline = lazyWithRetry(() =>
  importRetry(() => import('./pages/Attendance/UsersOnline'))
);

const Regularize = lazyWithRetry(() =>
  importRetry(() => import('./pages/Regularize/Regularize'))
);

const CheckAuthRoute = lazyWithRetry(() =>
  importRetry(() => import('./privateRoutes/checkAuthRoute'))
);

const Login = lazyWithRetry(() =>
  importRetry(() => import('./pages/Login/Login'))
);

const EmployeeTask = lazyWithRetry(() =>
  importRetry(() => import('./pages/Task/EmployeeTask'))
);

const AdminTask = lazyWithRetry(() =>
  importRetry(() => import('./pages/Task/AdminTask'))
);

const ForgotPassword = lazyWithRetry(() =>
  importRetry(() => import('./pages/ForgotPassword/ForgotPassword'))
);

const ResetConfirmation = lazyWithRetry(() =>
  importRetry(() => import('./pages/ResetConfirm/ResetConfirm'))
);

const ResetPassword = lazyWithRetry(() =>
  importRetry(() => import('./pages/ResetPassword/ResetPassword'))
);

const AssignRoles = lazyWithRetry(() =>
  importRetry(() => import('./components/Admin/AssignRoles/AssignRoles'))
);

const Permissions = lazyWithRetry(() =>
  importRetry(() => import('./pages/Admin/Permissions'))
);

const MonthlyView = lazyWithRetry(() =>
  importRetry(() => import('./pages/Attendance/MonthlyView'))
);

const SlackActivity = lazyWithRetry(() =>
  importRetry(() => import('./pages/SlackActivity/SlackActivity'))
);

const Events = lazyWithRetry(() =>
  importRetry(() => import('./pages/Events/Occasions'))
);

const Unauthorized = lazyWithRetry(() =>
  importRetry(() => import('./pages/Unauthorized/Unauthorized'))
);

/* ---------------- APP ---------------- */

const App: React.FC = () => {
  const { user, loading } = useContext(userContext);
  const location = useLocation();

  useEffect(() => {
    if (
      location.pathname === '/dashboard' ||
      location.pathname.startsWith('/dashboard/')
    ) {
      localStorage.setItem('currentPath', location.pathname);
    }
  }, [location]);

if (loading) {
  return (
    <div className="min-h-screen w-full bg-gray-50 flex items-center justify-center">
      <div className="relative flex items-center justify-center h-12 w-12">
        {/* Rotating ring around logo */}
        <div
          className="absolute inset-0 rounded-full animate-spin"
          style={{
            background: 'conic-gradient(#6535EB, #9F3198, transparent 70%)',
            padding: '3px',
          }}
        >
          <div className="h-full w-full rounded-full bg-gray-50" />
        </div>

        {/* Fixed logo */}
        <img
          src="/logo.png"
          alt="Loading"
          className="relative z-10 h-8 w-8 object-contain"
        />
      </div>
    </div>
  );
}

  return (
    <QueryClientProvider client={QUERY_CLIENT}>
      <GoogleOAuthProvider
        clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID as string}
      >
        <div>
          {user?.email &&
            location.pathname !== '/onboard' &&
            location.pathname !== '/inprocess' && <ChatBot />}

          <Suspense fallback={null}>
            <Routes>
              <Route
                path="/"
                element={
                  <Suspense fallback={null}>
                    <CheckAuthRoute>
                      <Login />
                    </CheckAuthRoute>
                  </Suspense>
                }
              />

              <Route path="/forgotpassword" element={<ForgotPassword />} />
              <Route
                path="/reset-password/:token"
                element={<ResetPassword />}
              />
              <Route
                path="/password-reset-confirmation"
                element={<ResetConfirmation />}
              />
              <Route
                path="dashboard/*"
                element={
                  <Suspense fallback={null}>
                    <Layout />
                  </Suspense>
                }
              >
                {/* HOME */}
                <Route
                  path=""
                  element={
                    <Suspense fallback={null}>
                      <Home />
                    </Suspense>
                  }
                />

                {/* ADMIN */}
                <Route path="admin/*">
                  <Route
                    path=""
                    element={
                      <ProtectedRoute allowedRoles={ROUTE_PERMISSIONS.admin}>
                        <Navigate to="assign-roles" />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="assign-roles"
                    element={
                      <ProtectedRoute allowedRoles={ROUTE_PERMISSIONS.admin}>
                        <AssignRoles />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="update-permissions"
                    element={
                      <ProtectedRoute allowedRoles={ROUTE_PERMISSIONS.admin}>
                        <Permissions />
                      </ProtectedRoute>
                    }
                  />
                </Route>

                {/* EMPLOYEES */}
                <Route
                  path="employees"
                  element={
                    <ProtectedRoute allowedRoles={ROUTE_PERMISSIONS.employees}>
                      <Employees />
                    </ProtectedRoute>
                  }
                />

                {/* PAYROLL */}
                <Route
                  path="payroll"
                  element={
                    <ProtectedRoute allowedRoles={ROUTE_PERMISSIONS.payroll}>
                      <SalarySlip />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="occasions"
                  element={
                    <ProtectedRoute allowedRoles={ROUTE_PERMISSIONS.occasions}>
                      <Events />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="leaveManagement"
                  element={
                    <ProtectedRoute
                      allowedRoles={ROUTE_PERMISSIONS.leaveManagement}
                    >
                      <LeaveManagement />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="slackStatus"
                  element={
                    <ProtectedRoute
                      allowedRoles={ROUTE_PERMISSIONS.slackStatus}
                    >
                      <SlackActivity />
                    </ProtectedRoute>
                  }
                />

                {/* DOCS */}
                <Route
                  path="companydocs"
                  element={
                    <ProtectedRoute
                      allowedRoles={ROUTE_PERMISSIONS.companyDocs}
                    >
                      <PolicyManager />
                    </ProtectedRoute>
                  }
                />

                {/* ATTENDANCE */}
                <Route
                  path="mark-attendance"
                  element={
                    <ProtectedRoute
                      allowedRoles={ROUTE_PERMISSIONS.markAttendance}
                    >
                      <Agent />
                    </ProtectedRoute>
                  }
                />

                <Route path="attendance-list/*">
                  <Route
                    path=""
                    element={
                      <ProtectedRoute
                        allowedRoles={ROUTE_PERMISSIONS.attendanceList}
                      >
                        <AttendanceList />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="monthly-view"
                    element={
                      <ProtectedRoute
                        allowedRoles={ROUTE_PERMISSIONS.monthlyView}
                      >
                        <MonthlyView />
                      </ProtectedRoute>
                    }
                  />
                </Route>

                <Route
                  path="view-attedence/:employeeId"
                  element={
                    <ProtectedRoute
                      allowedRoles={ROUTE_PERMISSIONS.viewAttendance}
                    >
                      <AttendenceDetail />
                    </ProtectedRoute>
                  }
                />

                {/* PROFILE */}
                <Route
                  path="profile"
                  element={
                    <ProtectedRoute allowedRoles={ROUTE_PERMISSIONS.profile}>
                      <Profile />
                    </ProtectedRoute>
                  }
                />

                {/* TASKS */}
                <Route
                  path="tasks/all"
                  element={
                    <ProtectedRoute allowedRoles={ROUTE_PERMISSIONS.tasksAll}>
                      <EmployeeTask />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="admin-tasks/team"
                  element={
                    <ProtectedRoute allowedRoles={ROUTE_PERMISSIONS.teamTasks}>
                      <AdminTask />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin-tasks/task/:employeeId"
                  element={
                    <ProtectedRoute allowedRoles={ROUTE_PERMISSIONS.teamTasks}>
                      <EmployeeTaskDetails />
                    </ProtectedRoute>
                  }
                />

                {/* SYSTEM */}
                <Route
                  path="online-users"
                  element={
                    <ProtectedRoute
                      allowedRoles={ROUTE_PERMISSIONS.onlineUsers}
                    >
                      <UsersOnline />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="regularize-attendance"
                  element={
                    <ProtectedRoute
                      allowedRoles={ROUTE_PERMISSIONS.regularizeAttendance}
                    >
                      <Regularize />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="/onboard" element={<Onboard />} />
              <Route path="/inprocess" element={<Inprocess />} />
              <Route path="*" element={<PageNotFound />} />
              <Route path="/unauthorized" element={<Unauthorized />} />
            </Routes>
          </Suspense>

          <Suspense fallback={null}>
            <ToastContainer />
          </Suspense>
        </div>
      </GoogleOAuthProvider>
    </QueryClientProvider>
  );
};

export default App;
